import {
  PRIORITIZED_DISPLAYABLE_CASH_CROPS_WITH_PREVENT_PLANT,
  PRIORITIZED_DISPLAYABLE_CASH_CROPS_WITHOUT_FALLOW,
  PRIORITY_CASH_CROPS,
  RDCashCropInput,
} from '@cibo/core'
import { AutocompleteField, ExtendedTheme } from '@cibo/ui'
import { Collapse, Divider, Grid2 as Grid, Stack, Typography, useTheme } from '@mui/material'
import InputLabel from '@mui/material/InputLabel'
import { FieldProps, Field as FormikField, useFormikContext } from 'formik'
import { ReactNode, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { DetailEditorPropsByYear } from '../types'
import { useIsRequired } from '../useIsRequired'
import { useShowDetailAttribute } from '../useShowDetailAttribute'
import { CashCropHarvestInputs } from './CashCropHarvestInputs'
import { CashCropPlantingInputs } from './CashCropPlantingInputs'
import { isFallow } from './isFallow'

const ConditionallyShow = ({ show, children }: { show: boolean; children: ReactNode }) =>
  show ? <>{children}</> : null

type CashCropEditorProps = DetailEditorPropsByYear & { simple?: boolean }

export const CashCropEditor = (props: CashCropEditorProps) => {
  const { filterInputs, name = 'cashCropEditor', requirement, year, simple = false } = props
  const { t } = useTranslation('@cibo/landmanager/CashCropEditor')
  const { t: cropT } = useTranslation('@cibo/ui/cropNames')
  const theme = useTheme<ExtendedTheme>()

  const benchmarkRequires = useIsRequired(requirement)
  const show = useShowDetailAttribute<RDCashCropInput>(filterInputs)

  const { values, setFieldValue } = useFormikContext<Record<string, any>>()

  useEffect(() => {
    setFieldValue(`${name}.preventPlant`, values[`${name}`]?.crop === 'preventPlant')
  }, [values[`${name}`]?.crop])

  return (
    <FormikField name={name} id={name}>
      {({
        field: {
          value: { crop, source } = {} as RDCashCropInput & {
            source?: string
          },
        },
        meta,
      }: FieldProps<RDCashCropInput & { source?: string }>) => {
        // @ts-ignore crop can be 'preventPlant' here even though it's not in the backend
        const hideCropExtras = !crop || isFallow(crop) || crop === 'preventPlant'

        const isCiboSourced = (subField: keyof RDCashCropInput) =>
          !!meta.initialValue?.[subField] &&
          source === 'cibo' &&
          meta.initialValue?.[subField] === meta.value[subField]

        return (
          <Stack padding={simple ? undefined : 2}>
            {!simple && <Typography paddingBottom={2}>{t('cta', { year })}</Typography>}
            <ConditionallyShow show={show('crop')}>
              <Stack paddingBottom={2}>
                <InputLabel required={!!benchmarkRequires('crop')} htmlFor={`${name}.crop`}>
                  {t('whatCrop')}
                </InputLabel>
                <AutocompleteField
                  sx={{
                    background: isCiboSourced('crop') ? theme.palette.ciboSensed : undefined,
                  }}
                  name={`${name}.crop`}
                  groupBy={c =>
                    PRIORITY_CASH_CROPS && PRIORITY_CASH_CROPS.includes(c)
                      ? t('suggested')
                      : t('all')
                  }
                  options={PRIORITIZED_DISPLAYABLE_CASH_CROPS_WITH_PREVENT_PLANT}
                  getOptionLabel={(option: string) => cropT(option)}
                  data-testid={`cashCrop.${year}.crop`}
                  selectOnFocus
                />
              </Stack>
            </ConditionallyShow>
            <Collapse
              in={crop === 'preventPlant'}
              style={simple && crop !== 'preventPlant' ? { marginTop: '0 !important' } : {}}
            >
              <Stack>
                <InputLabel required={!!benchmarkRequires('crop')} htmlFor={`${name}.crop`}>
                  {t('intendedCrop')}
                </InputLabel>
                <AutocompleteField
                  sx={{
                    background: isCiboSourced('crop') ? theme.palette.ciboSensed : undefined,
                  }}
                  name={`${name}.intendedCrop`}
                  groupBy={c =>
                    PRIORITY_CASH_CROPS && PRIORITY_CASH_CROPS.includes(c)
                      ? t('suggested')
                      : t('all')
                  }
                  options={PRIORITIZED_DISPLAYABLE_CASH_CROPS_WITHOUT_FALLOW}
                  getOptionLabel={(option: string) => cropT(option)}
                  data-testid={`cashCrop.${year}.intendedCrop`}
                  selectOnFocus
                />
              </Stack>
            </Collapse>
            <Collapse in={!hideCropExtras}>
              <ConditionallyShow show={show('plantedAt') || show('harvestedAt')}>
                {simple ? (
                  <Stack spacing={1}>
                    <Divider
                      sx={{
                        marginLeft: '-16px !important',
                        marginRight: '-16px !important',
                      }}
                    />
                    <CashCropPlantingInputs {...props} />
                    <Divider
                      sx={{
                        marginLeft: '-16px !important',
                        marginRight: '-16px !important',
                      }}
                    />
                    <CashCropHarvestInputs {...props} />
                  </Stack>
                ) : (
                  <Stack spacing={1}>
                    <Typography>{t('whatDates')}</Typography>
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <CashCropPlantingInputs {...props} />
                      </Grid>
                      <Grid size={6}>
                        <CashCropHarvestInputs {...props} />
                      </Grid>
                    </Grid>
                  </Stack>
                )}
              </ConditionallyShow>
            </Collapse>
          </Stack>
        )
      }}
    </FormikField>
  )
}
